// src/components/SquadDashboard.jsx

import React, { useState, useEffect } from 'react';
import { 
    TrendingUp, AlertTriangle, Loader2, Users,
    Target, CheckCircle2, Send, Eye, Wrench, Briefcase, ChevronDown
} from 'lucide-react';
import { db, functions } from '../config/firebase-config';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { SQUAD_PLAYBOOKS } from '../data/content';
import { useOrganization } from '../context/OrganizationContext';
import { useOrgVoice } from '../hooks/useOrgVoice';
import { heroClass, heroStyle, heroEyebrowStyle, isHubTheme } from '../utils/orgTheme';
import CoachPathwaysCard from './coach/CoachPathwaysCard';
import { resolveSupportPathways } from '../data/supportPathways';
import { CONTEXT_SIGNALS } from '../shared/scoringCore';
import TeamJoinLinkCard from './coach/TeamJoinLinkCard';

const SquadDashboard = ({ currentUser }) => {
    const { orgData, isPreviewMode } = useOrganization();
    
    // --- VOICE (single source: data/copyDictionary.js via useOrgVoice) ---
    const { isCorporate, t, adapt: adaptText } = useOrgVoice();
    const DICT = {
        title: t('squadTitle'),
        previewTitle: t('squadPreviewTitle'),
        memberLabelPlural: t('memberLabelPlural'),
        memberLabelSingle: t('memberLabelSingle'),
        noDataMsg: t('squadNoData'),
        habitTitle: t('habitTitle'),
        deployMsg: t('deployMsg'),
        planTitle: t('planTitle'),
        planSubtitle: t('planSubtitle'),
        Icon: isCorporate ? Briefcase : Target
    };

    // Admin/Preview Squad Selection State
    const [activeGroup, setActiveGroup] = useState('');
    // The teams this screen may show: every team while previewing; a coach's own
    // teams otherwise (groupIds when the server put them on several, else the one).
    const myTeams = isPreviewMode
        ? (orgData?.groups || [])
        : (Array.isArray(currentUser?.groupIds) && currentUser.groupIds.length ? currentUser.groupIds : (currentUser?.groupId ? [currentUser.groupId] : []));
    
    const [squadData, setSquadData] = useState({
        memberCount: 0, top3: [], bottom3: [], lowestMetric: null,
        respondents: 0, belowFloor: false, minCohort: null
    });
    const [isFetchingSquad, setIsFetchingSquad] = useState(true);
    const [fetchError, setFetchError] = useState(null); 
    
    const [deployedPlaybook, setDeployedPlaybook] = useState(null);
    const [isDeploying, setIsDeploying] = useState(false);
    const [previewMetric, setPreviewMetric] = useState(null);

    // Set the active group context based on role/preview
    useEffect(() => {
        if (isPreviewMode && orgData?.groups?.length > 0) {
            setActiveGroup(orgData.groups[0]); // Default to first squad in preview
        } else if (currentUser?.groupId) {
            setActiveGroup(currentUser.groupId);
        }
    }, [isPreviewMode, orgData, currentUser]);

    // FETCH SQUAD DATA
    useEffect(() => {
        const fetchSquadData = async () => {
            const targetOrgId = isPreviewMode ? orgData?.id : currentUser?.organizationId;
            
            if (!targetOrgId || !activeGroup) {
                setIsFetchingSquad(false);
                return;
            }

            setIsFetchingSquad(true);
            setFetchError(null);
            
            try {
                // Fetch currently deployed playbook
                let currentDeployed = null;
                const pbSnap = await getDoc(doc(db, "organizations", targetOrgId, "active_playbooks", activeGroup));
                if (pbSnap.exists()) {
                    currentDeployed = pbSnap.data().playbookId;
                    setDeployedPlaybook(currentDeployed);
                } else {
                    setDeployedPlaybook(null);
                }

                // Individual scores are locked to each user — squad numbers come
                // from the server-side aggregation endpoint, which only ever
                // returns cohort averages (and suppresses tiny cohorts).
                const aggFn = httpsCallable(functions, 'getGroupAggregates');
                const res = await aggFn({ orgId: targetOrgId, groupId: activeGroup });
                const groupAgg = res.data?.groups?.[activeGroup]
                    || Object.values(res.data?.groups || {})[0]
                    || { memberCount: 0, metrics: [] };

                const averages = (groupAgg.metrics || []).map(m => ({ id: m.id, score: m.score, count: m.count }));
                averages.sort((a,b) => b.score - a.score);
                const lowest = averages.length > 0 ? [...averages].reverse()[0].id : null;

                // How many people the averages actually rest on. Metrics under the
                // cohort floor are stripped server-side, so the best-covered metric
                // is a LOWER BOUND on respondents — never present it as exact when
                // the server says the group is suppressed.
                const respondents = averages.reduce((max, m) => Math.max(max, m.count || 0), 0);

                setSquadData({
                    memberCount: groupAgg.memberCount || 0,
                    top3: averages.slice(0, 3),
                    bottom3: [...averages].reverse().slice(0, 3),
                    lowestMetric: lowest,
                    respondents,
                    // Context signals (e.g. belonging): team averages the server has
                    // already floored; never part of the strengths / focus ranking.
                    context: Array.isArray(groupAgg.context) ? groupAgg.context : [],
                    belowFloor: !!groupAgg.suppressed,
                    // 'below_floor' = too few responses here; 'complementary' = this
                    // group is hidden to stop ANOTHER group being derived by
                    // subtraction. Saying "fewer than N responded" in the second case
                    // would be plainly false.
                    suppressionReason: groupAgg.suppressionReason || null,
                    minCohort: res.data?.minCohort || null
                });

                setPreviewMetric(currentDeployed || lowest);
                
            } catch (error) {
                console.error("Error fetching squad data:", error);
                setFetchError(error.message);
            } finally {
                setIsFetchingSquad(false);
            }
        };
        fetchSquadData();
    }, [currentUser, isPreviewMode, orgData, activeGroup]);

    const getFriendlyLabel = (key) => {
        const labels = {
            'focus': 'Focus', 'resilience': 'Resilience', 'motivation': 'Motivation',
            'confidence': 'Confidence', 'discipline': 'Discipline', 'awareness': 'Self-Awareness',
            'adaptability': 'Adaptability', 'sleep_perf': 'Sleep Quality',
            'nutrition': 'Nutrition', 'injury_mgmt': 'Pain Management'
        };
        return labels[key] || key;
    };

    const handleDeployPlaybook = async (playbookId) => {
        const targetOrgId = isPreviewMode ? orgData?.id : currentUser?.organizationId;
        if (!targetOrgId || !activeGroup || !playbookId) return;
        
        setIsDeploying(true);
        try {
            await setDoc(doc(db, "organizations", targetOrgId, "active_playbooks", activeGroup), {
                playbookId: playbookId,
                deployedAt: new Date().toISOString(),
                deployedBy: currentUser?.name || t('roleManager')
            });
            setDeployedPlaybook(playbookId);
            alert("Playbook deployed successfully!");
        } catch (error) {
            console.error("Deploy Error", error);
            alert("Failed to deploy playbook.");
        }
        setIsDeploying(false);
    };

    if (isFetchingSquad) {
        return <div className="flex justify-center items-center h-full"><Loader2 className="animate-spin text-brand-primary" size={40}/></div>;
    }

    if (fetchError) {
        return (
            <div className="flex items-center justify-center h-[60vh] animate-in fade-in">
                <div className="bg-red-50 p-12 rounded-3xl border border-red-200 text-center flex flex-col items-center shadow-sm max-w-lg">
                    <div className="w-20 h-20 bg-white text-red-500 rounded-full flex items-center justify-center mb-6 shadow-sm">
                        <AlertTriangle size={40} />
                    </div>
                    <h3 className="text-2xl font-black text-red-800 mb-2">Access Denied</h3>
                    <p className="text-red-600 text-sm font-medium mb-4">The database blocked your request. Please ensure permissions are correctly configured.</p>
                    <code className="text-[10px] bg-white text-red-400 p-3 rounded-lg border border-red-100">{fetchError}</code>
                </div>
            </div>
        );
    }

    if (!activeGroup) {
        return (
            <div className="flex items-center justify-center h-[60vh] animate-in fade-in">
                <div className="bg-white p-12 rounded-3xl border border-slate-200 text-center flex flex-col items-center shadow-sm max-w-lg">
                    <div className="w-20 h-20 bg-slate-50 text-slate-300 rounded-full flex items-center justify-center mb-6">
                        <Users size={40} />
                    </div>
                    <h3 className="text-2xl font-black text-slate-800 mb-2">No Group Found</h3>
                    <p className="text-slate-500 text-lg">{DICT.noDataMsg}</p>
                </div>
            </div>
        );
    }

    const activePlaybook = previewMetric ? SQUAD_PLAYBOOKS[previewMetric] : null;

    // Share of the group the averages actually rest on. Under two thirds, the
    // page says so rather than letting a manager read a partial signal as the
    // whole team's.
    const coveragePct = squadData.memberCount > 0
        ? (squadData.respondents / squadData.memberCount) * 100
        : 0;
    const coverageIsThin = squadData.respondents > 0 && coveragePct < 67;

    // A per-metric contributor count is only worth showing when that metric rests
    // on fewer people than the best-covered one — on every row it is just noise.
    const partialCount = (metric) =>
        typeof metric.count === 'number' && metric.count > 0 && metric.count < squadData.respondents;

    return (
        <div className="space-y-6 animate-in fade-in pb-20 max-w-6xl mx-auto">
            
            {/* SQUAD HEADER (Shape-shifting) */}
            <div className={`${heroClass(orgData)} rounded-3xl p-6 md:p-8 text-white flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative overflow-hidden ${isHubTheme(orgData) ? "shadow-sm" : "shadow-lg"}`} style={heroStyle(orgData)}>
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 w-full md:w-auto">
                    <div className="flex items-center gap-2 text-white/70 text-[11px] font-black uppercase tracking-widest mb-2" style={heroEyebrowStyle(orgData)}>
                        {isPreviewMode ? <Eye size={15} className="text-brand-accent"/> : <Users size={15}/>}
                        {isPreviewMode ? DICT.previewTitle : DICT.title}
                    </div>

                    {(isPreviewMode || myTeams.length > 1) ? (
                        // A native select at title size reads as a broken heading. Style it
                        // as what it is — a compact control — with our own chevron, since
                        // the OS arrow can't be themed against the brand band. A coach on
                        // several teams gets the same switcher, limited to their teams.
                        <div className="relative inline-block max-w-full">
                            <select
                                value={activeGroup}
                                onChange={(e) => setActiveGroup(e.target.value)}
                                aria-label={isPreviewMode ? 'Group being previewed' : 'Your team'}
                                className="appearance-none w-full bg-white/15 hover:bg-white/25 border border-white/25 text-white text-base md:text-lg font-black rounded-xl pl-4 pr-10 py-2.5 focus:ring-2 focus:ring-white/40 outline-none cursor-pointer transition-colors truncate"
                            >
                                {myTeams.map(g => <option key={g} value={g} className="text-slate-900">{g}</option>)}
                            </select>
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 pointer-events-none" />
                        </div>
                    ) : (
                        <h2 className="text-3xl md:text-4xl font-black tracking-tight">{activeGroup}</h2>
                    )}
                </div>
                <div className="bg-white/15 px-6 py-4 rounded-2xl border border-white/25 text-center relative z-10 backdrop-blur-sm shrink-0">
                    <p className="text-3xl font-black text-white tabular-nums">{squadData.memberCount}</p>
                    <p className="text-[10px] uppercase font-black text-white/70 tracking-widest">{DICT.memberLabelPlural}</p>
                </div>
            </div>

            {/* The team's join link, for a coach who is here and asks for it. Not the
                distribution path: links go out through the org's own channels from the
                roster import, so onboarding never waits on a coach logging in. */}
            {activeGroup && (isPreviewMode ? orgData?.id : currentUser?.organizationId) && (
                <TeamJoinLinkCard orgId={isPreviewMode ? orgData?.id : currentUser?.organizationId} team={activeGroup} hubName={orgData?.branding?.hubName || orgData?.name} />
            )}

            {squadData.memberCount === 0 && (
                <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200 text-amber-800 flex gap-3 shadow-sm">
                    <AlertTriangle size={24} className="shrink-0"/>
                    <p className="text-sm font-medium">Awaiting {DICT.memberLabelSingle} data. Averages will populate once team members complete their assessments.</p>
                </div>
            )}

            {/* PARTICIPATION — a manager's first question is who is actually
                responding. Averages over a third of the group are a different
                object than averages over all of it, and the page should say so
                before it shows a single score. */}
            {squadData.memberCount > 0 && (
                <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm">
                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                        <Users size={16}/> Participation
                    </h3>

                    {squadData.belowFloor ? (
                        // The floor hides the exact count on purpose — reporting a
                        // number here would leak what the suppression protects.
                        <p className="text-sm text-slate-600 font-medium leading-relaxed">
                            {squadData.suppressionReason === 'complementary'
                                ? 'Scores are hidden for this group so that a smaller group’s results cannot be worked out by subtraction.'
                                : `Fewer than ${squadData.minCohort || 5} ${DICT.memberLabelPlural.toLowerCase()} have current data, so scores stay hidden until more of the group checks in.`}
                        </p>
                    ) : (
                        // Full width now, so the count and the bar sit side by side
                        // rather than stacking into a tall, mostly-empty card.
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
                            <div className="flex items-baseline gap-2 shrink-0">
                                <span className="text-3xl font-black text-slate-900 tabular-nums leading-none">{squadData.respondents}</span>
                                <span className="text-sm font-bold text-slate-400">
                                    of {squadData.memberCount} {DICT.memberLabelPlural.toLowerCase()}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden mb-2">
                                    <div
                                        className={`h-full rounded-full transition-all ${coverageIsThin ? 'bg-amber-400' : 'bg-brand-primary'}`}
                                        style={{ width: `${Math.min(100, Math.round(coveragePct))}%` }}
                                    />
                                </div>
                                <p className="text-xs text-slate-500 font-medium leading-relaxed">
                                    {squadData.respondents === 0
                                        ? 'No one has completed an assessment yet — averages appear once they do.'
                                        : coverageIsThin
                                            ? `${Math.round(coveragePct)}% have current data. Averages may not represent the whole group yet.`
                                            : `${Math.round(coveragePct)}% have current data.`}
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* STRENGTHS & FOCUS AREAS — side by side, under participation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Top 3 Strengths */}
                <div className={`bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm ${squadData.memberCount === 0 ? 'opacity-50' : ''}`}>
                    <h3 className="text-[11px] font-black text-green-600 uppercase tracking-widest flex items-center gap-2 mb-4">
                        <TrendingUp size={16}/> Top Strengths
                    </h3>
                    <div className="space-y-2">
                        {squadData.top3.length > 0 ? squadData.top3.map((metric, idx) => (
                            <div key={idx} className="flex justify-between items-center gap-3 px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl">
                                <span className="flex items-center gap-2 min-w-0">
                                    <span className="font-bold text-slate-800 text-sm truncate">{getFriendlyLabel(metric.id)}</span>
                                    {partialCount(metric) && (
                                        <span className="text-[10px] font-bold text-slate-400 tabular-nums shrink-0" title={`Based on ${metric.count} responses`}>n={metric.count}</span>
                                    )}
                                </span>
                                <span className="font-black text-green-600 text-lg tabular-nums shrink-0">{metric.score}</span>
                            </div>
                        )) : <p className="text-sm text-slate-400 italic">No data yet.</p>}
                    </div>
                </div>

                {/* Bottom 3 Focus Areas */}
                <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm">
                    <div className="mb-4">
                        <h3 className="text-[11px] font-black text-orange-600 uppercase tracking-widest flex items-center gap-2 mb-1">
                            <AlertTriangle size={16}/> Focus Areas
                        </h3>
                        <p className="text-xs text-slate-500 font-medium">Select an area to preview its playbook.</p>
                    </div>
                    <div className="space-y-2">
                        {squadData.bottom3.length > 0 ? (
                            squadData.bottom3.map((metric, idx) => {
                                const isSelected = previewMetric === metric.id;
                                const isDeployed = deployedPlaybook === metric.id;

                                return (
                                    <button
                                        type="button"
                                        key={idx}
                                        onClick={() => setPreviewMetric(metric.id)}
                                        aria-pressed={isSelected}
                                        className={`w-full flex justify-between items-center gap-3 px-4 py-3 border rounded-2xl text-left transition-colors ${
                                            isSelected
                                            ? 'bg-orange-50 border-orange-300'
                                            : 'bg-slate-50 border-slate-100 hover:border-orange-200 hover:bg-white'
                                        }`}
                                    >
                                        <span className="flex items-center gap-2 min-w-0">
                                            <span className={`font-bold text-sm truncate ${isSelected ? 'text-orange-900' : 'text-slate-800'}`}>
                                                {getFriendlyLabel(metric.id)}
                                            </span>
                                            {partialCount(metric) && (
                                                <span className="text-[10px] font-bold text-slate-400 tabular-nums shrink-0" title={`Based on ${metric.count} responses`}>n={metric.count}</span>
                                            )}
                                            {isDeployed && (
                                                <span className="bg-green-100 text-green-700 text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider border border-green-200 shrink-0">
                                                    Active
                                                </span>
                                            )}
                                        </span>
                                        <span className={`font-black text-lg tabular-nums shrink-0 ${isSelected ? 'text-orange-600' : 'text-orange-500'}`}>
                                            {metric.score}
                                        </span>
                                    </button>
                                );
                            })
                        ) : (
                            // EMPTY STATE: Real clear indicator that there is no data yet
                            <div className="p-6 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50">
                                <p className="text-sm text-slate-500 font-medium mb-4">Awaiting assessment data to identify focus areas.</p>

                                {/* Dropdown just for admins/managers to preview playbooks while waiting for data */}
                                <div className="inline-block text-left">
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Preview a Playbook</label>
                                    <div className="relative">
                                        <select
                                            className="appearance-none w-full text-sm font-bold text-brand-primary pl-3 pr-9 py-2.5 border border-slate-200 hover:border-brand-primary/40 rounded-xl bg-white outline-none focus:ring-2 focus:ring-brand-primary/20 cursor-pointer transition-colors"
                                            onChange={(e) => setPreviewMetric(e.target.value)}
                                            value={previewMetric || ""}
                                        >
                                            <option value="" disabled>Select a topic...</option>
                                            {Object.keys(SQUAD_PLAYBOOKS).map(key => (
                                                <option key={key} value={key}>{getFriendlyLabel(key)}</option>
                                            ))}
                                        </select>
                                        <ChevronDown size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* TEAM CLIMATE - context signals. A team average under the same cohort
                floor as everything else on this page. */}
            {(squadData.context || []).length > 0 && (
                <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm">
                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                        <Users size={16}/> Team climate
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {squadData.context.map(c => (
                            <div key={c.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
                                <p className="text-sm font-black text-slate-900">{CONTEXT_SIGNALS[c.id]?.label || c.id}</p>
                                <p className="mt-1">
                                    <span className="text-3xl font-black text-slate-900 tabular-nums">{c.score}</span>
                                    <span className="text-sm font-bold text-slate-400"> / 100</span>
                                </p>
                                <p className="text-xs text-slate-500 font-medium mt-1 leading-relaxed">
                                    {CONTEXT_SIGNALS[c.id]?.coachNote} Team average from {c.count} {DICT.memberLabelPlural.toLowerCase()}. Individual answers are never shown.
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* PLAYBOOK PREVIEW + ROLLOUT — full width below the metrics */}
            <div className="lg:col-span-3 bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden flex flex-col h-full">
                <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none"></div>

                {activePlaybook ? (
                    <div className="flex flex-col h-full animate-in slide-in-from-right-4 duration-300">
                        <div className="flex items-center gap-2 text-brand-primary mb-2">
                            <DICT.Icon size={16}/>
                            <span className="font-black text-[11px] uppercase tracking-widest">
                                {deployedPlaybook === previewMetric ? 'Currently Active Playbook' : 'Playbook Preview'}
                            </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-2 leading-tight tracking-tight">{adaptText(activePlaybook.title)}</h3>
                        <p className="text-slate-600 text-sm leading-relaxed mb-6 max-w-xl">{adaptText(activePlaybook.description)}</p>

                        {/* 1. HABITS */}
                        <div className="relative z-10">
                            <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3">{DICT.habitTitle}</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {activePlaybook.actions.map((action, idx) => (
                                    <div key={idx} className="flex gap-3 items-start px-4 py-3 bg-slate-50 rounded-2xl border border-slate-100">
                                        <div className="w-7 h-7 rounded-xl bg-brand-light text-brand-primary flex items-center justify-center font-black shrink-0 text-xs mt-0.5">{idx + 1}</div>
                                        <div>
                                            <h5 className="font-bold text-slate-800 mb-0.5 text-sm leading-tight">{adaptText(action.title)}</h5>
                                            <p className="text-sm text-slate-500 leading-relaxed">{adaptText(action.text)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 2. ROLLOUT — the phases belong to the playbook above, so they
                            live in the same card rather than floating below the fold. */}
                        {activePlaybook.coachPlan && (
                            <div className="relative z-10 mt-6 pt-6 border-t border-slate-100">
                                <div className="flex items-center gap-2 mb-1">
                                    <Wrench size={16} className="text-brand-primary shrink-0"/>
                                    <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{DICT.planTitle}</h4>
                                </div>
                                <p className="text-xs text-slate-500 font-medium mb-3">{DICT.planSubtitle} {adaptText(activePlaybook.title)}</p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {activePlaybook.coachPlan.map((plan, idx) => (
                                        <div key={idx} className="px-4 py-3 rounded-2xl bg-slate-50 border border-slate-100">
                                            <div className="flex items-center justify-between gap-3 mb-1">
                                                <span className="text-[10px] font-black text-brand-primary uppercase tracking-widest truncate">{adaptText(plan.phase)}</span>
                                                <span className="text-[10px] font-bold text-slate-500 bg-white px-2 py-0.5 rounded-lg border border-slate-200 shrink-0">
                                                    {plan.duration}
                                                </span>
                                            </div>
                                            <h5 className="font-bold text-slate-800 text-sm mb-0.5 leading-tight">{adaptText(plan.title)}</h5>
                                            <p className="text-sm text-slate-500 leading-relaxed">{adaptText(plan.desc)}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* DEPLOY BUTTON */}
                        <button
                            onClick={() => handleDeployPlaybook(previewMetric)}
                            disabled={isDeploying || deployedPlaybook === previewMetric}
                            className={`w-full py-3.5 px-6 font-black rounded-2xl transition-colors flex justify-center items-center gap-2.5 mt-6 shrink-0 relative z-10 text-sm md:text-base shadow-lg ${
                                deployedPlaybook === previewMetric
                                ? 'bg-green-500 text-white shadow-green-500/25 border border-green-400'
                                : 'bg-brand-primary text-white hover:bg-brand-dark shadow-brand-primary/25'
                            }`}
                        >
                            {isDeploying ? <Loader2 size={18} className="animate-spin shrink-0"/> : (deployedPlaybook === previewMetric ? <CheckCircle2 size={18} className="shrink-0"/> : <Send size={18} className="shrink-0"/>)}
                            <span className="truncate leading-none">
                                {deployedPlaybook === previewMetric ? DICT.deployMsg : "Deploy Monthly Focus"}
                            </span>
                        </button>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-12 text-center min-h-[340px] animate-in fade-in">
                        <DICT.Icon size={44} className="mb-4 opacity-20"/>
                        <p className="text-sm font-medium">Select a Focus Area to preview its playbook.</p>
                    </div>
                )}
            </div>

            {/* HELP & SUPPORT PATHWAYS — what to do when a player comes to the
                coach. Org-configured (data/supportPathways.js). */}
            {resolveSupportPathways(orgData) && <CoachPathwaysCard pathways={resolveSupportPathways(orgData)} />}

        </div>
    );
};

export default SquadDashboard;